import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { add } from "date-fns";
import { Suspense, useEffect, useState } from "react";
import {
	Navigate,
	Route,
	BrowserRouter as Router,
	Routes,
} from "react-router-dom";

import { fetchAuthToken } from "./api/auth";
import { fetchPods } from "./api/pods";
import Loader from "./components/common/Loader";
import ErrorPage from "./components/error-page";
import { HomeLayout } from "./components/layout/Home";
import ConfigProvider from "./context/ConfigContext";
import { ConsumptionProvider } from "./context/ConsumptionContext";
import Budget from "./pages/budget/Budget";
import Consumption from "./pages/consumption/Consumption";
import NotifSettings from "./pages/notification/NotifSettings";
import TipDetail from "./pages/tips/TipDetail";
import Tips from "./pages/tips/Tips";
import type { Pod } from "./types";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 60 * 24,
			retry: 2,
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			refetchOnReconnect: false,
			refetchInterval: false,
			refetchIntervalInBackground: false,
		},
	},
});

export default function App() {
	const [authToken, setAuthToken] = useState<string | null>(null);
	const [pod, setPod] = useState<Pod | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		async function init() {
			if (window.location.pathname.includes("contract_not_found")) {
				setIsLoading(false);
				return;
			}

			const params = new URLSearchParams(window.location.search);
			const useCache = params.get("useCache");

			if (useCache === "0") {
				localStorage.removeItem("authToken");
				localStorage.removeItem("authTokenExpiresAt");
			}
			let token = localStorage.getItem("authToken");
			const expiresAtRaw = localStorage.getItem("authTokenExpiresAt");
			const nowPlus1h = add(new Date(), { hours: 1 });
			let isTokenValid = false;

			if (token && expiresAtRaw) {
				const expiresAt = new Date(expiresAtRaw);
				isTokenValid = expiresAt > nowPlus1h;
			}

			if (!token || !isTokenValid) {
				const tuyaCode = params.get("tuyacode");
				const appSchema = params.get("appschema");

				if (!tuyaCode || !appSchema) {
					window.location.href = "/contract_not_found";
					return;
				}

				const [newToken, error] = await fetchAuthToken(tuyaCode, appSchema);
				if (error || !newToken?.access_token) {
					window.location.href = "/contract_not_found";
					return;
				}

				token = newToken.access_token;
				localStorage.setItem("authToken", token);
				localStorage.setItem(
					"authTokenExpiresAt",
					add(new Date(), { days: 1 }).toISOString(),
				);
			}

			setAuthToken(token);

			const [pods, podsError] = await fetchPods(token);
			if (podsError || !Array.isArray(pods.pods) || pods.pods.length === 0) {
				window.location.href = "/contract_not_found";
				return;
			}

			setPod(pods.pods[0]);
			setIsLoading(false);
		}

		init();
	}, []);

	if (isLoading) return <Loader />;
	if (!authToken) return <ErrorPage reason="L'authentification a échoué" />;
	if (!pod) return <ErrorPage reason="Aucun Point de livraison trouvé" />;

	return (
		<QueryClientProvider client={queryClient}>
			<ConfigProvider bearerToken={authToken} pod={pod}>
				<ConsumptionProvider>
					<Router basename="/">
						<Suspense fallback={<Loader />}>
							<Routes>
								<Route path="/" element={<HomeLayout />}>
									<Route index element={<Navigate to="/consumption" />} />
									<Route path="consumption" element={<Consumption />} />
									<Route path="budget" element={<Budget />} />
									<Route path="setting" element={<NotifSettings />} />
									<Route path="tips" element={<Tips />} />
									<Route path="tips/:id" element={<TipDetail />} />
									<Route
										path="/contract_not_found"
										element={<ContractNotFound />}
									/>
								</Route>
							</Routes>
						</Suspense>
					</Router>
				</ConsumptionProvider>
			</ConfigProvider>
			<ReactQueryDevtools initialIsOpen={false} />
		</QueryClientProvider>
	);
}

function ContractNotFound() {
	return <div>Contract not found</div>;
}
